import { useState, useMemo, useEffect, useRef, type ReactNode } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { SearchFilterBar, Pagination, DataTable, type FilterDef, type ColumnDef, type SortState } from '../components';
import type { QuestionSet } from '../question-set-types';
import {
  kindOf, kindQuery, QUESTION_SET_KINDS, QUESTION_SET_KIND_LABELS,
  QUESTION_SET_KIND_LABELS_PLURAL, LIBRARY_PURPOSES,
  type QuestionSetKind,
} from '@padcanvas/shared';
import { programmeLabel } from '../reference-data';
import './TestLibrary.css';

const EXAM_OPTIONS = ['JEE', 'NEET', 'CBSE', 'ICSE', 'STATE'] as const;
const SUBJECT_OPTIONS = ['maths', 'physics', 'chemistry', 'biology'] as const;

/** Page size sent as the request `pageSize`; also used to derive totalPages. */
export const DEFAULT_TEST_LIBRARY_PAGE_SIZE = 25;

/** Interactive filter-bar state owned by TestLibrary. All filtering/search is
 *  done server-side now (the library is paginated and can be very large), so
 *  these are translated to request params and sent to `fetchTests`. */
export interface TestLibraryFilters {
  /** '' = every kind, else a `QuestionSetKind`. The one filter that says WHAT a
   *  paper is; exam/subject/class narrow within it. */
  type: string;
  exam: string;
  subject: string;
  class: string;
  status: string;  // '' | 'published' | 'draft'
  search: string;
}

/** One page returned by `fetchTests`: the rows plus the total matching count. */
export interface TestLibraryPage {
  questionSets: QuestionSet[];
  total: number;
}

/** Fetch one page (1-based) of tests for the given filter state + sort. */
export type FetchTestsPage = (filters: TestLibraryFilters, page: number, sort: TestLibrarySort | null) => Promise<TestLibraryPage>;

/** Translate filter-bar state into the backend list-request params shared by
 *  all three apps (admin/teacher/student). Consumers spread the result into
 *  their own request alongside scope flags (creatorOnly/publicOnly), page,
 *  pageSize, and any constant filters (e.g. the student's class/subjects). */
export function testLibraryQueryParams(f: TestLibraryFilters): {
  purpose?: string | readonly string[];
  pyqType?: string;
  exam?: string;
  subject?: string;
  class?: string;
  status?: 'draft' | 'published';
  search?: string;
} {
  // One kind selects one (purpose, pyqType) pair; no kind asks for the whole
  // library. Both come from `kindQuery`, which lives beside `kindOf` so the
  // filter and the badge can never name different sets of papers.
  const kind = QUESTION_SET_KINDS.includes(f.type as QuestionSetKind)
    ? kindQuery(f.type as QuestionSetKind)
    : undefined;
  return {
    purpose: kind?.purpose ?? LIBRARY_PURPOSES,
    pyqType: kind?.pyqType,
    exam: f.exam || undefined,
    subject: f.subject || undefined,
    class: f.class || undefined,
    // The Status filter only offers 'published'/'draft' (or '' = no filter).
    status: (f.status as 'draft' | 'published') || undefined,
    search: f.search.trim() || undefined,
  };
}

function formatDuration(seconds?: number): string | null {
  if (!seconds) return null;
  const mins = Math.round(seconds / 60);
  return mins >= 60 ? `${Math.round(mins / 60 * 10) / 10}h` : `${mins} min`;
}

export interface TestLibraryColumn {
  key: string;
  header: string;
  render: (test: QuestionSet) => ReactNode;
  tdClassName?: string;
  /** When set, the header is clickable and sorts server-side by this key
   *  (must be a backend-whitelisted sort key, e.g. 'updated', 'attempts'). */
  sortKey?: string;
}

/** Active sort, or null for the default (newest first). `by` is a backend
 *  sort key; `dir` toggles on repeated header clicks. */
export interface TestLibrarySort {
  by: string;
  dir: 'asc' | 'desc';
}

export interface TestLibrarySelection {
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  onToggleAll: (visibleIds: string[]) => void;
}

export interface TestLibraryProps {
  /** Fetch one page (1-based) for the given filters. Server-side filtered & paginated. */
  fetchTests: FetchTestsPage;
  /** React-query key. Filter state + page are appended automatically. */
  queryKey: string[];
  /** Called when a row is clicked */
  onRowClick?: (test: QuestionSet) => void;
  /** Render extra cell(s) at the end of each row (e.g. Start button) */
  renderRowActions?: (test: QuestionSet) => ReactNode;
  /** Extra columns inserted before the last standard column */
  extraColumns?: TestLibraryColumn[];
  /** Columns inserted immediately after Title, ahead of Subjects — for the
   *  identifying attributes a reader scans first (e.g. Class). */
  leadColumns?: TestLibraryColumn[];
  /** Override the subject options in the filter (e.g. from available data) */
  subjectOptions?: string[];
  /** Override the exam options — a student sees only the exams their profile
   *  targets, since the list beneath is already narrowed to those. Omitted ⇒
   *  the full list (admin / teacher discovery). */
  examOptions?: string[];
  /** Empty state message when no tests exist at all */
  emptyMessage?: string;
  /** Optional class filter. When provided, renders a class dropdown. */
  classOptions?: string[];
  /** When true, renders a Status (Published/Draft) filter dropdown. Admin-only —
   *  public discovery lists are always published. */
  showStatusFilter?: boolean;
  /** Offer the two chapter-scoped kinds — Topicwise PYQ and Topic Exercise — as
   *  filter options, and as tabs when `purposeTabs` is on.
   *
   *  It gates the CONTROLS, not the content: the rows arrive either way, and on
   *  dev 21 of the student library's 27 sets are Topicwise PYQs. An earlier
   *  version of this comment said topic exercises "must not surface in
   *  student/teacher discovery yet", which stopped being true the moment they
   *  were published — without the option, a student simply could not filter to
   *  the shelf they were already looking at. */
  showTopicExerciseFilter?: boolean;
  /** When true, the purpose filter (PYP / Mock Tests / Topic Exercises) is shown
   *  as a tab strip above the table instead of a dropdown, and drives the type
   *  filter. The Topic Exercises tab only appears when `showTopicExerciseFilter`
   *  is also set. */
  purposeTabs?: boolean;
  /** The exam/board scope this viewer is already narrowed to. When it names ONE,
   *  the Board column repeats that value on every row and the exam filter offers
   *  a choice with one outcome — so both are dropped. A student preparing for a
   *  single board should not be reading their own board back off every line.
   *  Absent or more than one leaves both in place. */
  scopedExams?: readonly string[];
  /** Pin the library to ONE kind, and let the page own the tab strip.
   *  The kind dropdown and the Kind column both go — the tab already says which
   *  kind this is, and repeating it in a filter and on every row is three places
   *  saying one thing. Mutually exclusive with `purposeTabs`. */
  kind?: QuestionSetKind;
  /** When set, renders a checkbox column and wires it to the given selection state. */
  selection?: TestLibrarySelection;
  /** Render extra filter controls above the table */
  renderExtraFilters?: () => ReactNode;
  /** When set, persists filter/page/sort to sessionStorage under this key so the
   *  view is restored after navigating into a test detail/edit and back. */
  storageKey?: string;
}

interface PersistedView {
  filterValues?: Record<string, string>;
  page?: number;
  sort?: TestLibrarySort | null;
  tab?: string;
}

function readPersistedView(storageKey?: string): PersistedView | null {
  if (!storageKey || typeof sessionStorage === 'undefined') return null;
  try {
    return JSON.parse(sessionStorage.getItem(storageKey) ?? 'null') as PersistedView | null;
  } catch {
    return null;
  }
}

/** Kind tabs (when `purposeTabs` is set) — each drives the `type` filter.
 *  Topic-exercise kinds are gated by `showTopicExerciseFilter`. */
const PURPOSE_TABS = [
  { value: 'past-paper', label: QUESTION_SET_KIND_LABELS_PLURAL['past-paper'] },
  { value: 'mock-test', label: QUESTION_SET_KIND_LABELS_PLURAL['mock-test'] },
  { value: 'topicwise-pyq', label: QUESTION_SET_KIND_LABELS_PLURAL['topicwise-pyq'], gated: true },
  { value: 'topic-exercise', label: QUESTION_SET_KIND_LABELS_PLURAL['topic-exercise'], gated: true },
] as const;

const LIBRARY_FILTERS: FilterDef[] = [
  {
    key: 'type',
    placeholder: 'All kinds',
    options: QUESTION_SET_KINDS.map(k => ({ value: k, label: QUESTION_SET_KIND_LABELS_PLURAL[k] })),
  },
  { key: 'exam', placeholder: 'All exams', options: EXAM_OPTIONS.map(x => ({ value: x, label: x })) },
  { key: 'subject', placeholder: 'All subjects', options: SUBJECT_OPTIONS.map(x => ({ value: x, label: x.charAt(0).toUpperCase() + x.slice(1) })) },
];

const EMPTY_FILTERS: TestLibraryFilters = { type: '', exam: '', subject: '', class: '', status: '', search: '' };

const STATUS_FILTER: FilterDef = {
  key: 'status',
  placeholder: 'All statuses',
  options: [{ value: 'published', label: 'Published' }, { value: 'draft', label: 'Draft' }],
};

export function TestLibrary({
  fetchTests,
  queryKey,
  onRowClick,
  renderRowActions,
  extraColumns,
  leadColumns,
  subjectOptions,
  examOptions,
  emptyMessage = 'No public tests available yet.',
  classOptions,
  showStatusFilter,
  showTopicExerciseFilter,
  purposeTabs,
  selection,
  renderExtraFilters,
  storageKey,
  scopedExams,
  kind,
}: TestLibraryProps) {
  /** One board means the column and the filter both say the same thing on every
   *  row. See `scopedExams`. */
  const singleExam = (scopedExams?.length ?? 0) === 1;

  // Restore a previously-persisted view (filters/page/sort/tab) once, on first mount.
  const restored = useRef<PersistedView | null>(readPersistedView(storageKey)).current;
  const [filterValues, setFilterValues] = useState<Record<string, string>>(
    restored?.filterValues ? { ...EMPTY_FILTERS, ...restored.filterValues } : { ...EMPTY_FILTERS }
  );
  const [page, setPage] = useState(restored?.page ?? 1);
  const [sort, setSort] = useState<TestLibrarySort | null>(restored?.sort ?? null);

  // Purpose tabs (PYP / Mock Tests / Topic Exercises) — the visible tabs, then
  // the active one. Drop the gated Topic Exercises tab unless enabled, and snap
  // a restored tab back to the first if it's no longer available.
  const tabs = useMemo(
    () => (kind ? [] : PURPOSE_TABS.filter(t => !('gated' in t && t.gated) || showTopicExerciseFilter)),
    [showTopicExerciseFilter, kind],
  );
  const [activeTab, setActiveTab] = useState<string>(
    tabs.some(t => t.value === restored?.tab) ? restored!.tab! : (tabs[0]?.value ?? ''),
  );

  // Debounce the free-text search so typing doesn't fire a request per keystroke.
  // Seed from the restored search so the first fetch already reflects it.
  const [debouncedSearch, setDebouncedSearch] = useState(restored?.filterValues?.search ?? '');
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(filterValues.search ?? ''), 300);
    return () => clearTimeout(id);
  }, [filterValues.search]);

  const effectiveFilters: TestLibraryFilters = {
    // When purpose tabs are on, the active tab drives the type filter (the
    // dropdown is hidden); otherwise the dropdown value is used.
    // A pinned kind wins over both: the page's own tab strip is the control.
    type: kind ?? (purposeTabs ? activeTab : (filterValues.type ?? '')),
    exam: filterValues.exam ?? '',
    subject: filterValues.subject ?? '',
    class: filterValues.class ?? '',
    status: filterValues.status ?? '',
    search: debouncedSearch,
  };

  // Any filter/search/sort change resets to the first page. Skip the very first
  // run so a restored page (from sessionStorage) isn't clobbered on mount.
  const isFirstReset = useRef(true);
  useEffect(() => {
    if (isFirstReset.current) {
      isFirstReset.current = false;
      return;
    }
    setPage(1);
  }, [effectiveFilters.type, effectiveFilters.exam, effectiveFilters.subject, effectiveFilters.class, effectiveFilters.status, effectiveFilters.search, sort?.by, sort?.dir]);

  // Persist the current view so navigating into a detail/edit and back restores it.
  useEffect(() => {
    if (!storageKey || typeof sessionStorage === 'undefined') return;
    sessionStorage.setItem(storageKey, JSON.stringify({ filterValues, page, sort, tab: activeTab }));
  }, [storageKey, filterValues, page, sort, activeTab]);

  const filters = useMemo(() => {
    // When purpose tabs are on, the type filter is rendered as tabs, not a dropdown.
    const base = LIBRARY_FILTERS
      .filter(f => !((purposeTabs || kind) && f.key === 'type'))
      // One board: the filter would offer a choice with one outcome, and the
      // column beside it already went for the same reason.
      .filter(f => !(singleExam && f.key === 'exam'))
      .map(f => {
      if (f.key === 'exam' && examOptions) {
        return { ...f, options: examOptions.map(e => ({ value: e, label: programmeLabel(e) })) };
      }
      if (f.key === 'subject' && subjectOptions) {
        return { ...f, placeholder: 'All subjects', options: subjectOptions.map(s => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) })) };
      }
      // The two chapter-scoped kinds are gated together: they are the same
      // content shelf, split by whether the questions came from real papers.
      if (f.key === 'type' && !showTopicExerciseFilter && 'options' in f) {
        return { ...f, options: f.options.filter(o => o.value !== 'topic-exercise' && o.value !== 'topicwise-pyq') };
      }
      return f;
    });
    if (classOptions && classOptions.length > 0) {
      base.push({
        key: 'class',
        placeholder: 'All classes',
        options: classOptions.map(c => ({ value: c, label: `Class ${c}` })),
      });
    }
    if (showStatusFilter) base.push(STATUS_FILTER);
    return base;
  }, [subjectOptions, examOptions, classOptions, showStatusFilter, showTopicExerciseFilter, purposeTabs, singleExam, kind]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [...queryKey, effectiveFilters, page, sort],
    queryFn: () => fetchTests(effectiveFilters, page, sort),
    placeholderData: keepPreviousData,
  });

  const rows = data?.questionSets ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / DEFAULT_TEST_LIBRARY_PAGE_SIZE));

  const hasActiveFilters = !!(effectiveFilters.type || effectiveFilters.exam || effectiveFilters.subject
    || effectiveFilters.class || effectiveFilters.status || effectiveFilters.search);

  const allSelected = selection && rows.length > 0
    && rows.every(t => selection.selectedIds.has(t.id));

  // Map the library's columns onto the shared DataTable's ColumnDef. Sortable
  // columns use the backend sort key as their `key` so DataTable's sort events
  // carry the right identifier. Selection + row-actions are modelled as columns
  // whose cells stop propagation so they don't trigger the row click.
  const columns = useMemo<ColumnDef<QuestionSet>[]>(() => {
    const cols: ColumnDef<QuestionSet>[] = [];
    if (selection) {
      cols.push({
        key: '__select',
        width: '32px',
        header: (
          <input
            type="checkbox"
            checked={!!allSelected}
            onChange={() => selection.onToggleAll(rows.map(t => t.id))}
          />
        ),
        render: t => (
          <span onClick={e => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={selection.selectedIds.has(t.id)}
              onChange={() => selection.onToggle(t.id)}
            />
          </span>
        ),
      });
    }
    cols.push(
      {
        key: 'title', header: 'Title', sortable: true, mobile: 'title',
        render: t => (
          <>
            <div className="tl-title">{t.title}</div>
            {t.description && <div className="tl-desc">{t.description}</div>}
          </>
        ),
      },
      // NOT sortable, deliberately. The exam lives in `targetExams[]` for 21 of
      // 27 library sets and in the scalar `targetExam` for 2, so no single Cosmos
      // path can order it — and an unmapped sort key falls back to createdAt,
      // which would be a header that silently sorts by something else. Sorting
      // this needs the two fields reconciled first.
      ...(singleExam ? [] : [{
        key: 'board', header: 'Board',
        render: (t: QuestionSet) => {
          // Whichever field carries this set's exam targeting — see the repo's
          // EXAM_MATCH_CLAUSE. A topic exercise with none is generic, not unknown.
          const single = t.targetExam ?? t.pypRef?.exam;
          const exams = t.targetExams?.length ? t.targetExams : single ? [single] : [];
          return exams.length ? exams.map(programmeLabel).join(', ') : <span className="tl-muted">—</span>;
        },
      } as ColumnDef<QuestionSet>]),
    );
    for (const c of leadColumns ?? []) {
      cols.push({
        key: c.sortKey ?? c.key,
        header: c.header,
        sortable: !!c.sortKey,
        className: c.tdClassName,
        render: c.render,
      });
    }
    cols.push(
      { key: 'subjects', header: 'Subjects', sortable: true, render: t => [...t.subjects].sort().map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(', ') },
    );
    // WHAT this paper is, read from purpose + pyqType rather than guessed from
    // the title. Without it a Topicwise PYQ and a whole past paper are two rows
    // that look identical, and the generated titles actively mislead — a chapter
    // exercise called `Physics-Sound-Chapter-PYQ-ICSE` reads as a past paper.
    // Dropped when the filter already names one kind: a column repeating the
    // filter on every row says nothing.
    const oneKind = QUESTION_SET_KINDS.includes(effectiveFilters.type as QuestionSetKind);
    if (!oneKind) {
      cols.push({
        key: 'kind', header: 'Kind', sortable: true,
        render: t => {
          const k = kindOf(t);
          return k
            ? <span className={`tl-kind tl-kind--${k}`}>{QUESTION_SET_KIND_LABELS[k]}</span>
            : <span className="tl-muted">—</span>;
        },
      });
    }
    // A topic exercise is never a previous-year paper and carries no time limit,
    // so both cells are always an em dash on those tabs — drop the columns
    // rather than show dead ones.
    const isTopicExerciseTab = effectiveFilters.type === 'topic-exercise'
      || effectiveFilters.type === 'topicwise-pyq';
    if (!isTopicExerciseTab) {
      cols.push({
        // The year is the first thing anyone asks a paper library for, and it was
        // only reachable by reading it out of the Paper label. It comes from the
        // sitting's own date rather than from when the set was created: the two
        // differ by years for anything imported.
        key: 'year', header: 'Year', sortable: true,
        render: t => t.pypRef?.date?.slice(0, 4) ?? <span className="tl-muted">—</span>,
      });
      cols.push({
        key: 'paper', header: 'Paper', sortable: true,
        render: t => (t.pyqType === 'FullPYP' ? (t.pypRef?.displayString ?? 'PYP') : <span className="tl-muted">—</span>),
      });
    }
    cols.push({ key: 'questions', header: 'Questions', sortable: true, render: t => t.totalQuestions });
    if (!isTopicExerciseTab) {
      cols.push({ key: 'duration', header: 'Duration', render: t => formatDuration(t.config.timeLimitSeconds) ?? '—' });
    }
    for (const c of extraColumns ?? []) {
      cols.push({
        key: c.sortKey ?? c.key,
        header: c.header,
        sortable: !!c.sortKey,
        className: c.tdClassName,
        render: c.render,
      });
    }
    if (renderRowActions) {
      cols.push({
        key: '__actions', header: '', mobile: 'accessory',
        render: t => <span onClick={e => e.stopPropagation()}>{renderRowActions(t)}</span>,
      });
    }
    return cols;
  }, [selection, allSelected, rows, extraColumns, leadColumns, effectiveFilters.type, renderRowActions, singleExam]);

  const pagination = total > DEFAULT_TEST_LIBRARY_PAGE_SIZE ? (
    <Pagination
      page={page}
      totalPages={totalPages}
      total={total}
      onPageChange={setPage}
      disabled={isFetching}
    />
  ) : undefined;

  return (
    <>
      {purposeTabs && (
        <div className="tl-tabs">
          {tabs.map(t => (
            <button
              key={t.value}
              type="button"
              className={`tl-tab ${activeTab === t.value ? 'active' : ''}`}
              onClick={() => setActiveTab(t.value)}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      <SearchFilterBar
        filters={filters}
        values={filterValues}
        onChange={setFilterValues}
        searchPlaceholder="Search tests..."
        loading={isFetching}
        trailingActions={pagination}
      />
      {renderExtraFilters?.()}

      <DataTable
        columns={columns}
        data={rows}
        rowKey={t => t.id}
        onRowClick={onRowClick}
        sort={sort ? { sortBy: sort.by, sortOrder: sort.dir } : undefined}
        onSortChange={(s: SortState) => setSort({ by: s.sortBy, dir: s.sortOrder })}
        // While the first load is in flight, show an empty body (no misleading
        // "no matches" copy) — the Fetch button conveys progress.
        emptyMessage={isLoading ? '' : (hasActiveFilters ? 'No tests match your filters.' : emptyMessage)}
        mobileCards
      />
    </>
  );
}
